import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CalendarDays, MapPin, User, Users, StickyNote, Loader2, ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PayNowButton, UnpaidFlag, isUnpaid } from "@/components/PayNowButton";
import { CancelEnrolment } from "@/components/CancelEnrolment";
import { CancelledSessionSummary } from "@/components/CancelledSessionSummary";
import { RescheduleSession } from "@/components/RescheduleSession";

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

function Row({ icon: Icon, children, testid }) {
  return (
    <div className="flex items-start gap-3 text-sm text-[#E5E5E5]" data-testid={testid}>
      <Icon className="w-4 h-4 text-[#A3A3A3] mt-0.5 shrink-0" /> <div>{children}</div>
    </div>
  );
}

export default function StudentSession() {
  const { id } = useParams();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  const load = () => api.get(`/me/sessions/${id}`).then((r) => setS(r.data)).catch((e) => setErr(formatApiError(e?.response?.data?.detail) || "Session not found"));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err) return <div className="p-8 text-sm text-[#FF4500]" data-testid="student-session-error">{err}</div>;
  if (!s) return <div className="p-8 text-sm text-[#A3A3A3] inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading session…</div>;
  const cancelled = s.status === "cancelled";
  const past = s.start && new Date(s.start) < new Date();
  if (cancelled) return <CancelledSessionSummary s={s} onReEnrolled={(oid) => (oid === id ? load() : nav(`/session/${oid}`))} />;
  return (
    <div className="max-w-2xl space-y-6" data-testid="student-session-page">
      <div>
        <div className="label-mono text-[#CCFF00] mb-2 flex items-center gap-2">
          {s.sport ? `${s.sport} session` : "Session"}
          {cancelled && <span className="text-[#FF4500]">· CANCELLED</span>}
          {isUnpaid(s) && <UnpaidFlag testid="student-session-unpaid" />}
        </div>
        <h1 className="h-display text-4xl sm:text-5xl" data-testid="student-session-title">{s.title}</h1>
      </div>
      <div className="card-surface p-5 space-y-3">
        <Row icon={CalendarDays} testid="student-session-when">{fmtWhen(s.start)} · {s.duration_min} min</Row>
        <Row icon={MapPin} testid="student-session-venue">{s.venue || "Venue to be confirmed"}</Row>
        <Row icon={User} testid="student-session-coach">{s.coach_name ? `Coach: ${s.coach_name}` : "Coach to be confirmed"}</Row>
        <Row icon={Users} testid="student-session-players">
          {s.other_players.length ? <>Also playing: {s.other_players.join(", ")}</> : "No other players enrolled yet"}
        </Row>
      </div>
      {s.session_notes && (
        <div className="card-surface p-5" data-testid="student-session-notes">
          <div className="label-mono mb-2 inline-flex items-center gap-2"><StickyNote className="w-3.5 h-3.5 text-[#CCFF00]" /> NOTES FROM YOUR COACH</div>
          <p className="text-sm text-[#E5E5E5] whitespace-pre-wrap">{s.session_notes}</p>
        </div>
      )}
      {isUnpaid(s) && (
        <div className="card-surface p-5 flex flex-wrap items-center justify-between gap-3 border-l-4 border-[#FF4500]">
          <div className="text-sm">Payment of £{Number(s.price || 0).toFixed(2)} is outstanding for this session.</div>
          <PayNowButton bookingId={s.booking_id} price={s.price} testid="student-session-pay" />
        </div>
      )}
      <div className="pt-4 border-t border-[#262626] flex flex-wrap items-center justify-between gap-3">
        <Link to="/calendar" className="text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white inline-flex items-center gap-1" data-testid="student-session-calendar-link">
          View in Calendar <ArrowRight className="w-3 h-3" />
        </Link>
        {!cancelled && !past && <CancelEnrolment bookingId={s.booking_id} paidByCredit={s.payment_status === "credit"} onCancelled={load} />}
      </div>
      {!cancelled && !past && s.session_type_key === "one_on_one" && (
        <RescheduleSession bookingId={s.booking_id} onDone={(nid) => (nid === id ? load() : nav(`/session/${nid}`))} testid="student-session-reschedule" />
      )}
      {!cancelled && !past && s.session_type_key !== "one_on_one" && (
        <div className="text-xs text-[#737373]" data-testid="student-session-no-reschedule">Need a different time? Group sessions can't be moved — cancel and re-book another session.</div>
      )}
    </div>
  );
}
